import { LinearGradient } from "expo-linear-gradient";
import { useEffect, useRef } from "react";
import { Animated, StyleSheet, TouchableOpacity, View } from "react-native";
import { Text } from "react-native-paper";
import Svg, { Path } from "react-native-svg";

import { CourseIcon } from "./svg/CourseIcon";

import { courseTheme } from "@/lib/training/courseTheme";

// Top appbar + footer CTA, ported from CPR_Course_Module1.html (.appbar lines
// 261-273, .cta lines 220-223 / 515-517). Colors come from courseTheme (the
// scoped navy palette) to match the source design.

type ChromeProps = {
    modulePill: string;
    progressPct: number;
    timeLeftLabel: string;
    stepLabel: string;
    canBack: boolean;
    onBack: () => void;
    onClose: () => void;
};

export function CoursePlayerChrome({
    modulePill,
    progressPct,
    timeLeftLabel,
    stepLabel,
    canBack,
    onBack,
    onClose
}: ChromeProps) {
    const width = useRef(new Animated.Value(progressPct)).current;

    useEffect(() => {
        const anim = Animated.timing(width, {
            toValue: progressPct,
            duration: 500,
            useNativeDriver: false
        });
        anim.start();
        return () => anim.stop();
    }, [width, progressPct]);

    const fillWidth = width.interpolate({
        inputRange: [0, 100],
        outputRange: ["0%", "100%"]
    });

    return (
        <View style={styles.appbar}>
            <View style={styles.appbarRow}>
                <TouchableOpacity
                    style={[styles.iconBtn, !canBack && styles.iconBtnDisabled]}
                    activeOpacity={0.7}
                    onPress={onBack}
                    disabled={!canBack}
                >
                    <Svg width={20} height={20} viewBox="0 0 24 24">
                        <Path
                            d="M15 18l-6-6 6-6"
                            stroke={courseTheme.navy}
                            strokeWidth={2.4}
                            fill="none"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </Svg>
                </TouchableOpacity>

                <View style={styles.pill}>
                    <Text style={styles.pillText}>{modulePill}</Text>
                </View>

                <TouchableOpacity style={styles.iconBtn} activeOpacity={0.7} onPress={onClose}>
                    <Svg width={20} height={20} viewBox="0 0 24 24">
                        <Path
                            d="M18 6L6 18M6 6l12 12"
                            stroke={courseTheme.navy}
                            strokeWidth={2.4}
                            fill="none"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </Svg>
                </TouchableOpacity>
            </View>

            <View style={styles.metaRow}>
                <View style={styles.ptrack}>
                    <Animated.View style={[styles.pfill, { width: fillWidth }]} />
                </View>
                <View style={styles.timeleft}>
                    <CourseIcon id="clock" size={14} color={courseTheme.slate} />
                    <Text style={styles.timeleftText}>{timeLeftLabel}</Text>
                </View>
            </View>

            <Text style={styles.stepcount}>{stepLabel}</Text>
        </View>
    );
}

type CtaProps = {
    label: string;
    showArrow: boolean;
    disabled: boolean;
    onPress: () => void;
};

export function CourseFooterCta({ label, showArrow, disabled, onPress }: CtaProps) {
    return (
        <View style={styles.footer}>
            <TouchableOpacity
                activeOpacity={0.85}
                onPress={onPress}
                disabled={disabled}
                style={styles.ctaTouchable}
            >
                {disabled ? (
                    <View style={[styles.cta, styles.ctaDisabled]}>
                        <Text style={[styles.ctaText, styles.ctaTextDisabled]}>{label}</Text>
                    </View>
                ) : (
                    <CtaGradient label={label} showArrow={showArrow} />
                )}
            </TouchableOpacity>
        </View>
    );
}

function CtaGradient({ label, showArrow }: { label: string; showArrow: boolean }) {
    return (
        <LinearGradient
            colors={[courseTheme.teal, courseTheme.tealD]}
            start={{ x: 0, y: 0 }}
            end={{ x: 0, y: 1 }}
            style={styles.cta}
        >
            <Text style={styles.ctaText}>{label}</Text>
            {showArrow ? <CourseIcon id="chevronRight" size={20} color="#FFFFFF" /> : null}
        </LinearGradient>
    );
}

const styles = StyleSheet.create({
    appbar: {
        paddingHorizontal: 18,
        paddingTop: 8,
        paddingBottom: 12,
        borderBottomWidth: 1,
        borderBottomColor: courseTheme.line,
        backgroundColor: courseTheme.white
    },
    appbarRow: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between"
    },
    iconBtn: {
        width: 34,
        height: 34,
        borderRadius: 11,
        backgroundColor: courseTheme.paper,
        alignItems: "center",
        justifyContent: "center"
    },
    iconBtnDisabled: { opacity: 0.4 },
    pill: {
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 99,
        backgroundColor: courseTheme.paper
    },
    pillText: {
        fontSize: 12,
        fontWeight: "700",
        color: courseTheme.navy
    },
    metaRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 12,
        marginTop: 12
    },
    ptrack: {
        flex: 1,
        height: 5,
        borderRadius: 99,
        backgroundColor: courseTheme.line,
        overflow: "hidden"
    },
    pfill: {
        height: "100%",
        borderRadius: 99,
        backgroundColor: courseTheme.teal
    },
    timeleft: {
        flexDirection: "row",
        alignItems: "center",
        gap: 5
    },
    timeleftText: {
        fontSize: 12,
        fontWeight: "600",
        color: courseTheme.slate
    },
    stepcount: {
        marginTop: 8,
        fontSize: 11,
        fontWeight: "600",
        color: courseTheme.slateL
    },
    footer: {
        paddingHorizontal: 18,
        paddingTop: 12,
        paddingBottom: 18,
        borderTopWidth: 1,
        borderTopColor: courseTheme.line,
        backgroundColor: courseTheme.white
    },
    ctaTouchable: { width: "100%" },
    cta: {
        width: "100%",
        borderRadius: 15,
        paddingVertical: 16,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 9
    },
    ctaDisabled: { backgroundColor: courseTheme.line },
    ctaText: {
        fontSize: 15.5,
        fontWeight: "700",
        color: "#FFFFFF"
    },
    ctaTextDisabled: { color: courseTheme.slateL }
});
